import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Icon } from './icons';

export interface ToastOptions {
  /** Past tense, names the thing: "Marked “Debt schedule” optional". */
  message: string;
  /** Usually Undo — offered only where the action can really be reversed. */
  action?: { label: string; onSelect: () => void };
}

interface ToastItem extends ToastOptions {
  id: number;
}

const ToastContext = createContext<((t: ToastOptions) => void) | null>(null);

/** Show a toast. Outside a ToastProvider (e.g. a render test) it is a no-op. */
export function useToast(): (t: ToastOptions) => void {
  return useContext(ToastContext) ?? noop;
}
function noop() {}

const MAX_TOASTS = 3;

/**
 * The toast region (DataRoom UI "Toast"): confirms an action that already
 * happened and offers Undo where it can be undone. One polite live region,
 * bottom-left on desktop and full-width at the bottom on a phone; a toast stays
 * 8s with an action (4s without), pauses while hovered or focused, never steals
 * focus, and at most three stack (newest at the bottom). Errors are not toasts —
 * they stay inline where the problem is.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const show = useCallback((t: ToastOptions) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { ...t, id }].slice(-MAX_TOASTS));
  }, []);
  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[70] flex flex-col items-stretch gap-2 p-3 sm:inset-x-auto sm:left-6 sm:bottom-6 sm:items-start sm:p-0"
      >
        {toasts.map((t) => (
          <Toast key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function Toast({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const [paused, setPaused] = useState(false);
  const ttl = toast.action ? 8000 : 4000;
  // The parent re-creates onDismiss each render; keep the timer stable.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(() => dismissRef.current(), ttl);
    return () => clearTimeout(timer);
  }, [paused, ttl]);

  return (
    <div
      className="pointer-events-auto flex min-h-12 w-full items-center gap-4 rounded-lg bg-inverse py-2 pl-4 pr-2 text-sm text-inverse-fg shadow-md sm:max-w-[480px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="min-w-0 flex-1">{toast.message}</span>
      {toast.action ? (
        <button
          type="button"
          className="h-8 rounded-md px-2.5 font-bold text-inverse-action hover:bg-white/10"
          onClick={() => {
            toast.action?.onSelect();
            onDismiss();
          }}
        >
          {toast.action.label}
        </button>
      ) : null}
      <button
        type="button"
        aria-label="Dismiss"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md opacity-75 hover:opacity-100"
        onClick={onDismiss}
      >
        <Icon name="close" />
      </button>
    </div>
  );
}
